<template>
  <div class="min-h-screen bg-gradient-to-b from-slate-50 to-white">
    <!-- Hard OOS Pending Banner -->
    <div class="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-3">
        <div class="flex flex-col sm:flex-row items-center justify-center gap-2 text-center sm:text-left">
          <span class="text-lg">⚠️</span>
          <div>
            <span class="font-bold text-sm sm:text-base">Hard OOS Validation Pending &mdash;</span>
            <span class="text-sm sm:text-base opacity-95"> Results below are Soft-OOS validation split (Jan 2023 &ndash; Dec 2025). Live-market hard OOS validation is scheduled for Q4 2026. This page will be updated upon completion.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div class="text-center">
          <div class="flex flex-wrap justify-center gap-3 mb-6">
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-sm font-semibold">Comprehensive Benchmark Analysis</span>
            </div>
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/30 backdrop-blur-sm rounded-full border border-emerald-400/40">
              <span class="text-sm font-bold text-emerald-200">✨ Phase 3 Retrain #7 — Now Live</span>
            </div>
          </div>
          
          <h1 class="text-5xl font-bold mb-4">
            How InvestBuddy Compares to Major ETFs
          </h1>
          <p class="text-xl opacity-90 max-w-3xl mx-auto">
            Retrain #7 architecture vs RSP equal-weight benchmark · Phase 2 comprehensive multi-ETF analysis (2022–2025)
          </p>
        </div>
      </div>
    </div>

    <!-- Phase 3 Hero Banner (Asterisk + Anchor approach) -->
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl -mt-8">
      <!-- Phase 3 Primary Banner -->
      <div class="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white rounded-2xl shadow-2xl p-8 mb-4">
        <div class="text-center mb-6">
          <div class="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-bold mb-2">
            🔬 Retrain #7 · Phase 3 Multitask LSTM · Current Production Model
          </div>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-5 gap-4 text-center mb-6">
          <div class="bg-white/10 rounded-xl p-4">
            <div class="text-4xl font-bold mb-1">+60.8%<sup class="text-lg">*</sup></div>
            <div class="text-sm opacity-90">Total Return</div>
            <div class="text-xs opacity-70 mt-1">Jan 2023 – Dec 2025</div>
          </div>
          <div class="bg-white/10 rounded-xl p-4">
            <div class="text-4xl font-bold mb-1">+25.1pp</div>
            <div class="text-sm opacity-90">Alpha vs RSP</div>
            <div class="text-xs opacity-70 mt-1">Equal-Weight S&P 500</div>
          </div>
          <div class="bg-white/10 rounded-xl p-4">
            <div class="text-4xl font-bold mb-1">60.78%</div>
            <div class="text-sm opacity-90">OOS Win Rate</div>
            <div class="text-xs opacity-70 mt-1">Direction accuracy</div>
          </div>
          <div class="bg-white/10 rounded-xl p-4">
            <div class="text-4xl font-bold mb-1">47k</div>
            <div class="text-sm opacity-90">Parameters</div>
            <div class="text-xs opacity-70 mt-1">↓67% vs Phase 2</div>
          </div>
          <div class="bg-white/10 rounded-xl p-4 col-span-2 md:col-span-1">
            <div class="text-4xl font-bold mb-1">0.78</div>
            <div class="text-sm opacity-90">Sharpe Ratio</div>
            <div class="text-xs opacity-70 mt-1">vs RSP 0.64</div>
          </div>
        </div>
        <!-- Anchor disclaimer -->
        <div class="border-t border-white/20 pt-4 text-center">
          <p class="text-xs text-white/75 leading-relaxed">
            <sup>*</sup> <strong class="text-white/90">Soft-OOS Validation Split (Jan 2023 – Dec 2025).</strong> Model trained through May 3, 2026 — this backtest predates the training cutoff and represents validation-split quality, not hard out-of-sample performance.
            <strong class="text-amber-200">Hard OOS live-market validation scheduled Q4 2026.</strong>
            RSP was chosen as the benchmark because equal-weight vs equal-weight is the structurally honest comparison for a 10-stock portfolio.
          </p>
        </div>
      </div>

      <!-- Phase 2 Comprehensive Banner (multi-ETF, dynamically loaded) -->
      <div class="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl shadow-xl p-6">
        <div class="text-xs font-semibold text-slate-400 mb-3 text-center uppercase tracking-widest">Phase 2 Comprehensive Analysis · Monthly Rebalancing · 2022–2025</div>
        <div v-if="!loading" class="grid md:grid-cols-4 gap-4 text-center">
          <div>
            <div class="text-2xl font-bold mb-1">{{ alphaVsVOO >= 0 ? '+' : '' }}{{ alphaVsVOO.toFixed(2) }}%</div>
            <div class="text-xs opacity-80">Alpha vs VOO (S&P 500)</div>
          </div>
          <div>
            <div class="text-2xl font-bold mb-1">{{ alphaVsRSP >= 0 ? '+' : '' }}{{ alphaVsRSP.toFixed(2) }}%</div>
            <div class="text-xs opacity-80">Alpha vs RSP (Equal Weight)</div>
          </div>
          <div>
            <div class="text-2xl font-bold mb-1">{{ alphaVsQQQ >= 0 ? '+' : '' }}{{ alphaVsQQQ.toFixed(2) }}%</div>
            <div class="text-xs opacity-80">Alpha vs QQQ (Nasdaq-100)</div>
          </div>
          <div>
            <div class="text-2xl font-bold mb-1">{{ alphaVs6040 >= 0 ? '+' : '' }}{{ alphaVs6040.toFixed(2) }}%</div>
            <div class="text-xs opacity-80">Alpha vs 60/40 Portfolio</div>
          </div>
        </div>
        <div v-else class="text-center py-2">
          <div class="animate-pulse text-sm">Loading performance data...</div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
      
      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <span class="ml-4 text-slate-600">Loading benchmark data...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div class="text-red-600 text-lg font-semibold mb-2">Failed to load benchmark data</div>
        <div class="text-red-500 text-sm">{{ error }}</div>
        <button 
          @click="fetchData" 
          class="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Retry
        </button>
      </div>

      <!-- Data Display -->
      <div v-else>
        <!-- Title -->
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-slate-800 mb-2">Phase 2 Monthly Performance Comparison (2022&ndash;2025)</h2>
          <p class="text-slate-600">Phase 2 backtest: $10,000 across {{ totalSnapshots }} snapshots rebalanced every 30 trading days. <span class="text-emerald-600 font-semibold">Phase 3 (Retrain #7) uses weekly rebalancing &mdash; see the Phase 3 section above.</span></p>
        </div>

        <!-- Alpha Explanation Banner -->
        <div class="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-6">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/>
                <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-xl font-bold text-emerald-900 mb-3">📊 Understanding "Alpha" (Excess Return)</h3>
              <div class="bg-white rounded-lg p-4 mb-3 border-2 border-emerald-300">
                <p class="text-slate-700 font-semibold text-sm mb-2">Alpha Formula:</p>
                <div class="font-mono text-base font-bold bg-gradient-to-r from-emerald-100 to-teal-100 p-3 rounded-lg border-2 border-emerald-400 text-center text-slate-900">
                  Alpha = InvestBuddy Return % − Benchmark Return %
                </div>
              </div>
              <div class="grid md:grid-cols-2 gap-3 text-sm">
                <div class="bg-white rounded-lg p-3 border border-emerald-200">
                  <p class="font-bold text-emerald-700 mb-1">✅ Example: Your Data</p>
                  <p class="text-slate-600">• InvestBuddy: <span class="font-bold text-green-600">+{{ summary.final_returns?.investbuddy.toFixed(2) }}%</span></p>
                  <p class="text-slate-600">• VOO (S&P 500): <span class="font-bold text-blue-600">+{{ summary.final_returns?.voo.toFixed(2) }}%</span></p>
                  <p class="text-slate-600 mt-2">• <strong>Alpha:</strong> <span class="font-bold text-emerald-600">{{ summary.final_returns?.investbuddy.toFixed(2) }}% - {{ summary.final_returns?.voo.toFixed(2) }}% = +{{ alphaVsVOO.toFixed(2) }}%</span></p>
                </div>
                <div class="bg-white rounded-lg p-3 border border-emerald-200">
                  <p class="font-bold text-emerald-700 mb-1">📈 What This Means:</p>
                  <p class="text-slate-600">Final values ($10K start):</p>
                  <p class="text-slate-600">• InvestBuddy: <span class="font-bold">${{ (10000 * (1 + summary.final_returns?.investbuddy / 100)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</span></p>
                  <p class="text-slate-600">• VOO: <span class="font-bold">${{ (10000 * (1 + summary.final_returns?.voo / 100)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</span></p>
                  <p class="text-slate-600 mt-2"><strong>InvestBuddy earned you ${{ ((10000 * (1 + summary.final_returns?.investbuddy / 100)) - (10000 * (1 + summary.final_returns?.voo / 100))).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }} more</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Explanation Box -->
        <div class="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-blue-900 mb-2">How to Read This Table</h3>
              <div class="text-blue-800 space-y-2 text-sm">
                <p><strong>📊 Starting Point:</strong> Each strategy started with the same $10,000 investment on {{ summary.start_date }}</p>
                <p><strong>📅 Rebalancing:</strong> Portfolio reviewed every <strong>30 trading days</strong> (≈42-45 calendar days due to weekends/holidays)</p>
                <p class="text-xs italic text-blue-700 ml-4">⚠️ Note: Dates aren't exactly 30 days apart because the backtest uses trading days only. 30 trading days ≈ 6 weeks calendar time.</p>
                <p><strong>🎯 The Top 5 Rotation:</strong> The LSTM model continuously ranks a 50-stock universe by predicted momentum. At each 30-day interval, the AI <strong>liquidates any holding that has fallen out of the Top 5</strong> — that's the implicit <span class="text-red-600 font-semibold">SELL signal</span> — and reallocates the freed capital equally into the new highest-probability momentum leaders (<span class="text-green-700 font-semibold">BUY signal</span>). Each position is sized at exactly <strong>1/5 of total portfolio value</strong>. If the entire market turns negative, the model issues an absolute SELL and rotates the full portfolio to cash.</p>
                <p class="text-xs italic text-blue-700 ml-4">💎 <strong>Transparency:</strong> Every pick shown is verifiable — paste the tickers and date into any charting platform to independently confirm the backtest results.</p>
                <p><strong>💸 Transaction Costs:</strong> InvestBuddy results include realistic friction: <strong>$1.00 flat fee per trade</strong> + <strong>0.05% slippage per transaction</strong>. ETF benchmarks (VOO, RSP, QQQ, 60/40) assume $0 commission — making InvestBuddy's outperformance more conservative.</p>
                <p class="mt-3"><strong>💡 Toggle Benchmarks:</strong> Click "Show Institutional Benchmarks" to reveal RSP (Equal Weight), QQQ (Nasdaq-100), and 60/40 Portfolio columns</p>
              </div>
            </div>
          </div>
        </div>

        <!-- How Rebalancing Actually Works Section -->
        <div class="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <h3 class="text-xl font-bold text-emerald-900 mb-2">🔄 Phase 2: How Monthly Rebalancing Works in Practice</h3>
          <p class="text-sm text-emerald-800 mb-4">This Phase 2 dataset uses a 30-day rebalance cadence (aligned to the Phase 2 model's 10-day prediction horizon). <strong>Phase 3 (Retrain #7) switches to weekly rebalancing</strong> because the model's primary signal is now day-5, making 5 trading days the structurally correct hold period.</p>
          <p class="text-sm font-semibold text-emerald-900 mb-3">Here is how a user executes the Phase 2 monthly AI output:</p>
          <div class="space-y-3 mb-5">
            <div class="flex items-start gap-3 bg-white rounded-lg p-4 border-l-4 border-green-500">
              <span class="text-lg">🟢</span>
              <div>
                <p class="text-sm font-bold text-green-800">The Hold</p>
                <p class="text-sm text-slate-700">If a stock (like CVX) was in your portfolio last month and the AI keeps it in the Top 5 this month, you do nothing. You simply hold it.</p>
              </div>
            </div>
            <div class="flex items-start gap-3 bg-white rounded-lg p-4 border-l-4 border-red-500">
              <span class="text-lg">🔴</span>
              <div>
                <p class="text-sm font-bold text-red-800">The Implicit Sell</p>
                <p class="text-sm text-slate-700">If a stock loses its momentum and drops out of the Top 5, that is your sell signal. You liquidate that specific position.</p>
              </div>
            </div>
            <div class="flex items-start gap-3 bg-white rounded-lg p-4 border-l-4 border-blue-500">
              <span class="text-lg">🔵</span>
              <div>
                <p class="text-sm font-bold text-blue-800">The Buy</p>
                <p class="text-sm text-slate-700">You take the cash from your sold positions and buy the new stocks that just entered the Top 5, restoring your equal-weight balance.</p>
              </div>
            </div>
          </div>
          <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p class="text-xs text-amber-800"><strong>📋 Note:</strong> Our backtest math assumes a worst-case scenario (100% portfolio turnover every 30 days) to ensure our $21.68 fee penalty is as aggressive as possible. In reality, because the AI often holds winning stocks for multiple months, your actual transaction friction will be lower.</p>
          </div>
        </div>

        <!-- Benchmark Explanations -->
        <div class="mb-6 bg-slate-50 border border-slate-200 rounded-xl p-6">
          <h3 class="text-xl font-bold text-slate-800 mb-4">📚 Understanding Each Benchmark</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <!-- VOO -->
            <div class="bg-white rounded-lg p-5 border-l-4 border-blue-500">
              <h4 class="text-lg font-bold text-blue-700 mb-2">📈 S&P 500 ETF (VOO)</h4>
              <p class="text-sm text-slate-600 mb-2"><strong>What it measures:</strong> Market-cap weighted performance of the 500 largest US companies</p>
              <p class="text-sm text-slate-600 mb-2"><strong>Goal:</strong> Track the broad US stock market benchmark used by institutional investors</p>
              <p class="text-sm text-slate-600"><strong>Analysis:</strong> If InvestBuddy's alpha is positive, it means AI stock selection beat the passive market approach</p>
            </div>

            <!-- RSP -->
            <div class="bg-white rounded-lg p-5 border-l-4 border-purple-500">
              <h4 class="text-lg font-bold text-purple-700 mb-2">⚖️ Equal Weight ETF (RSP)</h4>
              <p class="text-sm text-slate-600 mb-2"><strong>What it measures:</strong> S&P 500 companies with equal allocation (no big-cap bias)</p>
              <p class="text-sm text-slate-600 mb-2"><strong>Goal:</strong> Test if AI selection beats democratized portfolio weighting</p>
              <p class="text-sm text-slate-600"><strong>Analysis:</strong> Large alpha shows AI picks higher-quality stocks vs spreading capital equally</p>
            </div>

            <!-- QQQ -->
            <div class="bg-white rounded-lg p-5 border-l-4 border-orange-500">
              <h4 class="text-lg font-bold text-orange-700 mb-2">💻 Nasdaq-100 ETF (QQQ)</h4>
              <p class="text-sm text-slate-600 mb-2"><strong>What it measures:</strong> Top 100 non-financial Nasdaq stocks (tech-heavy)</p>
              <p class="text-sm text-slate-600 mb-2"><strong>Goal:</strong> Compare AI sector rotation vs pure tech momentum strategy</p>
              <p class="text-sm text-slate-600"><strong>Analysis:</strong> Positive alpha proves intelligent diversification beats tech concentration</p>
            </div>

            <!-- 60/40 -->
            <div class="bg-white rounded-lg p-5 border-l-4 border-teal-500">
              <h4 class="text-lg font-bold text-teal-700 mb-2">🛡️ 60/40 Portfolio</h4>
              <p class="text-sm text-slate-600 mb-2"><strong>What it measures:</strong> Traditional balanced portfolio (60% stocks/VOO, 40% bonds/AGG)</p>
              <p class="text-sm text-slate-600 mb-2"><strong>Goal:</strong> Test AI cash fallback vs traditional bond protection</p>
              <p class="text-sm text-slate-600"><strong>Analysis:</strong> Large alpha shows cash-based risk management outperforms fixed income drag</p>
            </div>
          </div>
        </div>

        <!-- Toggle Button (Moved Closer to Table) -->
        <div class="mb-4 flex justify-end">
          <button
            @click="toggleInstitutionalBenchmarks"
            class="px-6 py-3 rounded-lg font-semibold transition-all shadow-lg"
            :class="showInstitutionalBenchmarks 
              ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
              : 'bg-white text-indigo-600 border-2 border-indigo-600 hover:bg-indigo-50'"
          >
            {{ showInstitutionalBenchmarks ? 'Hide' : 'Show' }} Institutional Benchmarks
          </button>
        </div>

        <!-- AG Grid Table Component -->
        <div class="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
          <div style="height: 700px;" class="ag-theme-alpine">
            <AgGridVue
              :columnDefs="columnDefs"
              :rowData="displaySnapshots"
              :defaultColDef="defaultColDef"
              :animateRows="true"
              :pagination="true"
              :paginationPageSize="20"
              @grid-ready="onGridReady"
              class="w-full h-full"
            />
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid md:grid-cols-3 gap-6 mt-12">
          <div class="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
            <h3 class="text-lg font-semibold text-slate-800 mb-4">Final Returns</h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-slate-600">InvestBuddy</span>
                <span class="text-green-600 font-bold">+{{ summary.final_returns?.investbuddy.toFixed(2) }}%</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-slate-600">VOO (S&P 500)</span>
                <span class="text-blue-600 font-bold">+{{ summary.final_returns?.voo.toFixed(2) }}%</span>
              </div>
              <div v-if="showInstitutionalBenchmarks" class="flex justify-between items-center">
                <span class="text-slate-600">RSP (Equal Weight)</span>
                <span class="text-purple-600 font-bold">+{{ summary.final_returns?.rsp.toFixed(2) }}%</span>
              </div>
              <div v-if="showInstitutionalBenchmarks" class="flex justify-between items-center">
                <span class="text-slate-600">QQQ (Nasdaq-100)</span>
                <span class="text-orange-600 font-bold">+{{ summary.final_returns?.qqq.toFixed(2) }}%</span>
              </div>
              <div v-if="showInstitutionalBenchmarks" class="flex justify-between items-center">
                <span class="text-slate-600">60/40 Portfolio</span>
                <span class="text-teal-600 font-bold">+{{ summary.final_returns?.['60_40'].toFixed(2) }}%</span>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
            <h3 class="text-lg font-semibold text-slate-800 mb-4">Test Period</h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-slate-600 text-sm">Start Date</span>
                <span class="font-bold text-slate-800 text-lg">{{ summary.start_date || 'Loading...' }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-slate-600 text-sm">End Date</span>
                <span class="font-bold text-slate-800 text-lg">{{ summary.end_date || 'Loading...' }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-slate-600 text-sm">Total Snapshots</span>
                <span class="font-bold text-slate-800 text-lg">{{ summary.total_months || 0 }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-slate-600 text-sm">Rebalance Frequency</span>
                <span class="font-bold text-indigo-600 text-lg">Every 30 days <span class="text-xs font-normal text-slate-400">(Phase 2)</span></span>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold mb-4">What This Proves (Phase 2)</h3>
            <ul class="space-y-2 text-sm">
              <li class="flex items-start">
                <svg class="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span>Phase 2 (monthly rebalancing, 2022–2025) beat equal-weight RSP by +87.39pp</span>
              </li>
              <li class="flex items-start">
                <svg class="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span>Phase 3 Retrain #7 (hysteresis, 2023–2025) beat RSP by +25.05pp at 0.78 Sharpe</span>
              </li>
              <li class="flex items-start">
                <svg class="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span>47k-parameter model (67% leaner) eliminates Epoch 1 memorization, forces genuine generalization</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Why Monthly Rebalancing? Section -->
        <!-- Phase 3 Architecture Update Banner -->
        <div class="mt-16 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl shadow-lg p-8">
          <div class="flex items-center gap-3 mb-6">
            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold shadow-lg text-xl">
              ✨
            </div>
            <div>
              <h2 class="text-2xl font-bold text-slate-900">Phase 3 Architecture Update (May 2026)</h2>
              <p class="text-sm text-slate-500">New pipeline with persistence hysteresis, tested against RSP — the harder equal-weight benchmark</p>
            </div>
          </div>

          <div class="grid md:grid-cols-2 gap-8">
            <!-- Equity Curve -->
            <div>
              <div class="rounded-xl overflow-hidden bg-white border border-slate-200 shadow-md">
                <img
                  src="/benchmarks/rsp_benchmark_hysteresis_phase3_results.png"
                  alt="Phase 3: InvestBuddy vs RSP (Jan 2023 – Dec 2025)"
                  class="w-full h-auto"
                />
              </div>
              <div class="mt-3 flex flex-wrap items-center justify-center gap-4 text-xs">
                <div class="flex items-center gap-2"><div class="w-3 h-3 bg-blue-500 rounded"></div><span class="text-slate-600">RSP Equal-Weight Benchmark</span></div>
                <div class="flex items-center gap-2"><div class="w-3 h-3 bg-red-500 rounded"></div><span class="text-slate-600">InvestBuddy Phase 3</span></div>
              </div>
            </div>

            <!-- Metrics & Description -->
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-white rounded-xl p-4 border border-emerald-200 text-center shadow-sm">
                  <div class="text-xs text-slate-500 mb-1">Model Return</div>
                  <div class="text-3xl font-bold text-emerald-700">+60.80%</div>
                </div>
                <div class="bg-white rounded-xl p-4 border border-slate-200 text-center shadow-sm">
                  <div class="text-xs text-slate-500 mb-1">RSP Return</div>
                  <div class="text-3xl font-bold text-blue-600">+35.74%</div>
                </div>
                <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 text-center shadow-sm">
                  <div class="text-xs text-slate-500 mb-1">Alpha vs RSP</div>
                  <div class="text-3xl font-bold text-green-700">+25.05pp</div>
                </div>
                <div class="bg-white rounded-xl p-4 border border-purple-200 text-center shadow-sm">
                  <div class="text-xs text-slate-500 mb-1">Sharpe</div>
                  <div class="text-2xl font-bold text-purple-700">0.78 <span class="text-sm text-slate-500">vs 0.64</span></div>
                </div>
              </div>

              <div class="bg-white rounded-xl p-4 border border-slate-200">
                <p class="text-sm font-bold text-slate-800 mb-2">Five Structural Fixes That Unlocked This</p>
                <ul class="text-sm text-slate-600 space-y-1">
                  <li>🔄 <strong>Persistence Hysteresis</strong> — hold winners until signal actually dies</li>
                  <li>📊 <strong>Confidence threshold</strong> raised 15% → 25%</li>
                  <li>🚫 <strong>$5 price floor</strong> — blocks penny-trap microstructure</li>
                  <li>📅 <strong>7-day earnings blackout</strong> — avoids binary event risk</li>
                  <li>⚡ <strong>Head-conflict gate</strong> — direction vs magnitude must agree</li>
                </ul>
              </div>

              <div class="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p class="text-xs font-semibold text-amber-800">⚠️ Soft OOS Disclosure</p>
                <p class="text-xs text-amber-700 mt-1">Model trained through May 2026. This 2023–2025 window predates the training cutoff (validation-split quality). Hard OOS test runs September 2026.</p>
              </div>
            </div>
          </div>

          <blockquote class="mt-6 border-l-4 border-emerald-400 pl-5 py-2">
            <p class="text-slate-700 italic text-sm leading-relaxed">
              "The $5 price floor kept you out of the graveyard, and the hysteresis let your ML edge actually manifest as dollars. When you run this exact same script in September for the Hard OOS window, if the red line is still above the blue line, you are ready to deploy real capital."
            </p>
            <footer class="mt-2 text-xs text-slate-500 font-semibold">— Independent ML Consultant, May 12, 2026</footer>
          </blockquote>
        </div>

        <!-- Why Monthly Rebalancing? Section -->
        <div class="mt-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
          <div class="text-center mb-10">
            <div class="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 backdrop-blur-sm rounded-full border border-red-400/30 mb-4">
              <svg class="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
              <span class="text-sm font-semibold text-red-400">🛡️ Institutional Risk Discipline</span>
            </div>
            <h2 class="text-4xl font-bold mb-4">Phase 2: Why Monthly Beat Weekly</h2>
            <p class="text-xl text-slate-300 max-w-3xl mx-auto">
              In Phase 2 (10-day prediction model), the math was unambiguous &mdash; <em>monthly rebalancing dominated</em>. Phase 3 changed the architecture and the correct cadence with it.
            </p>
          </div>

          <!-- Comparison Table -->
          <div class="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700 overflow-hidden mb-8">
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="bg-slate-700/50 border-b border-slate-600">
                    <th class="px-6 py-4 text-left text-sm font-bold text-slate-200">Metric</th>
                    <th class="px-6 py-4 text-center text-sm font-bold text-emerald-400">Monthly (30 days)</th>
                    <th class="px-6 py-4 text-center text-sm font-bold text-amber-400">Weekly (7 days)</th>
                    <th class="px-6 py-4 text-center text-sm font-bold text-slate-300">Impact</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-700/50">
                  <!-- Sharpe first: Monthly wins, green highlight -->
                  <tr class="bg-emerald-500/10 hover:bg-emerald-500/20 transition border-2 border-emerald-500/40">
                    <td class="px-6 py-4 text-emerald-300 font-bold">🏆 Sharpe Ratio</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">0.95</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-bold text-lg">0.46</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-semibold">Monthly +0.49 better risk-adjusted ✓</td>
                  </tr>
                  <!-- Costs: danger red -->
                  <tr class="bg-red-500/10 hover:bg-red-500/20 transition border-t-2 border-red-500/50">
                    <td class="px-6 py-4 text-slate-300 font-bold">Transaction Costs</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">$533</td>
                    <td class="px-6 py-4 text-center text-red-400 font-bold text-lg">$1,224</td>
                    <td class="px-6 py-4 text-center text-red-400 font-bold">Weekly costs +$691 (+130%)</td>
                  </tr>
                  <tr class="bg-red-500/10 hover:bg-red-500/20 transition border-b-2 border-red-500/50">
                    <td class="px-6 py-4 text-slate-300 font-bold">Cost as % of Capital</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">5.33%</td>
                    <td class="px-6 py-4 text-center text-red-400 font-bold text-lg">12.24%</td>
                    <td class="px-6 py-4 text-center text-red-400 font-bold">+6.91% more friction</td>
                  </tr>
                  <!-- Neutral rows: Weekly wins shown in muted gray -->
                  <tr class="hover:bg-slate-700/30 transition">
                    <td class="px-6 py-4 text-slate-300 font-medium">Number of Trades</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">241</td>
                    <td class="px-6 py-4 text-center text-slate-300 font-bold text-lg">573</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-semibold">Weekly +332 trades (+138%)</td>
                  </tr>
                  <tr class="hover:bg-slate-700/30 transition">
                    <td class="px-6 py-4 text-slate-300 font-medium">Max Drawdown</td>
                    <td class="px-6 py-4 text-center text-red-400 font-bold text-lg">-37.51%</td>
                    <td class="px-6 py-4 text-center text-red-400 font-bold text-lg">-37.63%</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-semibold">−0.12% (similar depth)</td>
                  </tr>
                  <tr class="hover:bg-slate-700/30 transition">
                    <td class="px-6 py-4 text-slate-300 font-medium">Total Return</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">+106.12%</td>
                    <td class="px-6 py-4 text-center text-slate-300 font-bold text-lg">+117.33%</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-semibold">Weekly +11.21% higher</td>
                  </tr>
                  <tr class="hover:bg-slate-700/30 transition">
                    <td class="px-6 py-4 text-slate-300 font-medium">Final Value</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">$20,612.38</td>
                    <td class="px-6 py-4 text-center text-slate-300 font-bold text-lg">$21,732.68</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-semibold">Weekly +$1,120 (+5.4%)</td>
                  </tr>
                  <tr class="hover:bg-slate-700/30 transition">
                    <td class="px-6 py-4 text-slate-300 font-medium">Alpha vs VOO</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">+63.41pp</td>
                    <td class="px-6 py-4 text-center text-slate-300 font-bold text-lg">+73.51pp</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-semibold">Weekly +10.10pp higher</td>
                  </tr>
                  <tr class="hover:bg-slate-700/30 transition">
                    <td class="px-6 py-4 text-slate-300 font-medium">Alpha vs QQQ</td>
                    <td class="px-6 py-4 text-center text-emerald-400 font-bold text-lg">+53.36pp</td>
                    <td class="px-6 py-4 text-center text-slate-300 font-bold text-lg">+63.12pp</td>
                    <td class="px-6 py-4 text-center text-slate-400 font-semibold">Weekly +9.76pp higher</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Key Insights -->
          <div class="grid md:grid-cols-3 gap-6 mb-8">
            <div class="bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur rounded-xl p-6 border border-orange-500/30">
              <div class="flex items-start gap-3 mb-3">
                <div class="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <span class="text-xl font-bold">⚡</span>
                </div>
                <h3 class="text-lg font-bold text-orange-300">The Physics of the Sharpe Collapse</h3>
              </div>
              <p class="text-slate-300 text-sm leading-relaxed">
                Weekly sampling captures the chaotic <span class="text-orange-400 font-bold">high-frequency noise</span> of daily price swings. Monthly sampling smooths that noise into a measurable trend. The result: <span class="text-red-400 font-bold">Sharpe 0.95 → 0.46</span> — you take on more than double the volatility for only +$1,120 in extra return. In institutional finance, <span class="text-emerald-400 font-bold">0.95 on a long-only strategy is elite</span>. 0.46 is marginal.
              </p>
            </div>

            <div class="bg-gradient-to-br from-red-500/20 to-rose-500/20 backdrop-blur rounded-xl p-6 border border-red-500/30">
              <div class="flex items-start gap-3 mb-3">
                <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <span class="text-xl font-bold">✂️</span>
                </div>
                <h3 class="text-lg font-bold text-red-300">Phase 2: The 10/7 Timing Mismatch</h3>
              </div>
              <p class="text-slate-300 text-sm leading-relaxed">
                The Phase 2 LSTM predicted a <span class="text-white font-bold">10-day forward horizon</span>. Weekly rebalancing forced an exit every <span class="text-red-400 font-bold">7 days</span> &mdash; three days before momentum was expected to materialize. <span class="text-emerald-400 font-bold">Phase 3 Retrain #7 changed this</span>: the primary signal is now <strong>day-5</strong>, so <span class="text-emerald-400 font-bold">5 trading days (weekly) is the structurally correct hold period</span> &mdash; no mismatch.
              </p>
            </div>

            <div class="bg-gradient-to-br from-emerald-500/20 to-green-500/20 backdrop-blur rounded-xl p-6 border border-emerald-500/30">
              <div class="flex items-start gap-3 mb-3">
                <div class="flex-shrink-0 w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                  <span class="text-xl font-bold">🛡️</span>
                </div>
                <h3 class="text-lg font-bold text-emerald-300">Phase 3: Architecture-Driven Cadence</h3>
              </div>
              <p class="text-slate-300 text-sm leading-relaxed">
                Phase 3 Retrain #7 uses <span class="text-emerald-400 font-bold">persistence hysteresis</span>: positions are held until their own signal dies, not until a calendar date. When no signal dies, zero trades happen &mdash; cost stays near zero. When stocks rotate out, you trade. This means Phase 3 can use weekly as the <em>check frequency</em> without incurring the full weekly cost burden. The cadence follows the signal, not a schedule.
              </p>
            </div>
          </div>

          <!-- Bottom Line -->
          <div class="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur rounded-xl p-6 border-2 border-indigo-500/30">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span class="text-2xl">💡</span>
                </div>
              </div>
              <div class="flex-1">
                <div class="text-indigo-300 font-semibold mb-2">💡 The Phase 2 Bottom Line</div>
                <p class="text-slate-200 text-lg leading-relaxed border-l-4 border-indigo-500 pl-4">
                  Phase 2's model survived a <span class="text-yellow-300 font-bold">12.24% capital drag from weekly friction</span> and still dominated the S&P 500 by +73.51pp — confirming genuine, persistent signal. But the higher Sharpe (0.95 vs 0.46) at monthly frequency made monthly the right choice for Phase 2's 10-day prediction window.
                </p>
                <div class="text-slate-400 text-sm mt-3">
                  Phase 3 Retrain #7 changed the equation: day-5 predictions + hysteresis = <span class="text-emerald-400 font-bold">weekly is now the structurally correct cadence</span>. The principle hasn't changed — match your rebalance to your model's horizon.
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom CTA -->
          <div class="mt-8 text-center">
            <div class="inline-flex items-center gap-2 text-slate-400 text-sm">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span>Phase 2 (30-day cadence) achieved elite Sharpe 0.95. Phase 3 Retrain #7 uses weekly (5 trading days) aligned to the day-5 prediction horizon.</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Transaction Fee Anatomy Section -->
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl mt-12 pb-8">
        <div class="py-16 bg-slate-900 rounded-2xl overflow-hidden">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <!-- Header -->
          <div class="text-center mb-10">
            <div class="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-2 mb-4">
              <span class="text-red-400 text-sm font-semibold">📐 Fee Anatomy</span>
            </div>
            <h2 class="text-3xl font-bold text-white mb-3">The Brutal Friction of Overtrading</h2>
            <p class="text-slate-400 max-w-2xl mx-auto">
              Every single rebalance costs the same ~$21.68 in combined commissions + slippage. See how trading 4× more often drains your capital and destroys your risk-adjusted efficiency.
            </p>
          </div>

          <!-- Per-rebalance cost breakdown -->
          <div class="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 mb-8">
            <h3 class="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <span class="text-yellow-400">⚗️</span> Cost Per Rebalance (5-stock portfolio, avg ~$11,700)
            </h3>
            <div class="grid md:grid-cols-2 gap-8">
              <!-- Sell side -->
              <div>
                <div class="text-slate-400 text-xs uppercase tracking-wider mb-3 font-semibold">On every SELL (5 positions)</div>
                <div class="space-y-2">
                  <div class="flex justify-between items-center bg-slate-700/50 rounded-lg px-4 py-2.5">
                    <span class="text-slate-300 text-sm">Broker commission × 5</span>
                    <span class="text-red-400 font-bold">$5.00</span>
                  </div>
                  <div class="flex justify-between items-center bg-slate-700/50 rounded-lg px-4 py-2.5">
                    <span class="text-slate-300 text-sm">Slippage 0.05% × 5 positions</span>
                    <span class="text-red-400 font-bold">~$5.84</span>
                  </div>
                  <div class="flex justify-between items-center bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5">
                    <span class="text-slate-200 text-sm font-semibold">Sell-side subtotal</span>
                    <span class="text-red-400 font-bold">~$10.84</span>
                  </div>
                </div>
              </div>
              <!-- Buy side -->
              <div>
                <div class="text-slate-400 text-xs uppercase tracking-wider mb-3 font-semibold">On every BUY (5 new positions)</div>
                <div class="space-y-2">
                  <div class="flex justify-between items-center bg-slate-700/50 rounded-lg px-4 py-2.5">
                    <span class="text-slate-300 text-sm">Broker commission × 5</span>
                    <span class="text-red-400 font-bold">$5.00</span>
                  </div>
                  <div class="flex justify-between items-center bg-slate-700/50 rounded-lg px-4 py-2.5">
                    <span class="text-slate-300 text-sm">Slippage 0.05% × 5 positions</span>
                    <span class="text-red-400 font-bold">~$5.84</span>
                  </div>
                  <div class="flex justify-between items-center bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5">
                    <span class="text-slate-200 text-sm font-semibold">Buy-side subtotal</span>
                    <span class="text-red-400 font-bold">~$10.84</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Total -->
            <div class="mt-6 flex items-center justify-between bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl px-6 py-4">
              <div>
                <div class="text-white font-bold text-lg">Total cost per rebalance</div>
                <div class="text-slate-400 text-sm">$10 flat + ~$11.68 slippage on avg $11,700 portfolio</div>
              </div>
              <div class="text-3xl font-black text-red-400">~$21.68</div>
            </div>
          </div>

          <!-- The multiplier effect -->
          <div class="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 mb-8">
            <h3 class="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <span class="text-orange-400">✖️</span> The Multiplier Effect (Walk-Forward Period: 2022 – 2025)
            </h3>
            <div class="grid md:grid-cols-2 gap-6">
              <!-- Monthly -->
              <div class="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
                <div class="text-emerald-400 font-bold text-sm uppercase tracking-wider mb-4">Monthly schedule (30 days)</div>
                <div class="space-y-3">
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">Total Trades (Buys + Sells)</span>
                    <span class="text-white font-bold">241</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">Direct Broker Commissions ($1/trade)</span>
                    <span class="text-white font-bold">$241.00</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">Invisible Slippage Drag (~0.05%)</span>
                    <span class="text-white font-bold">~$291.87</span>
                  </div>
                  <div class="border-t border-emerald-500/30 pt-3 flex justify-between">
                    <span class="text-emerald-300 font-bold">Total Friction Cost</span>
                    <span class="text-emerald-400 font-black text-xl">~$532.87</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">As % of $10K start</span>
                    <span class="text-emerald-400 font-bold">5.33%</span>
                  </div>
                </div>
              </div>
              <!-- Weekly -->
              <div class="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <div class="text-red-400 font-bold text-sm uppercase tracking-wider mb-4">Weekly schedule (7 days)</div>
                <div class="space-y-3">
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">Total Trades (Buys + Sells)</span>
                    <span class="text-white font-bold">573</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">Direct Broker Commissions ($1/trade)</span>
                    <span class="text-white font-bold">$573.00</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">Invisible Slippage Drag (~0.05%)</span>
                    <span class="text-white font-bold">~$650.62</span>
                  </div>
                  <div class="border-t border-red-500/30 pt-3 flex justify-between">
                    <span class="text-red-300 font-bold">Total Friction Cost</span>
                    <span class="text-red-400 font-black text-xl">~$1,223.62</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-slate-400">As % of $10K start</span>
                    <span class="text-red-400 font-bold">12.24%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- The delta -->
            <div class="mt-6 grid md:grid-cols-3 gap-4">
              <div class="bg-slate-700/50 rounded-xl p-5 text-center">
                <div class="text-slate-400 text-xs uppercase tracking-wider mb-1">Extra fees (weekly vs monthly)</div>
                <div class="text-3xl font-black text-red-400">+$691</div>
                <div class="text-slate-400 text-xs mt-1">2.3× more in fee drag</div>
              </div>
              <div class="bg-slate-700/50 rounded-xl p-5 text-center">
                <div class="text-slate-400 text-xs uppercase tracking-wider mb-1">Extra cost (% of capital)</div>
                <div class="text-3xl font-black text-orange-400">+6.91%</div>
                <div class="text-slate-400 text-xs mt-1">12.24% vs 5.33% for monthly</div>
              </div>
              <div class="bg-slate-700/50 rounded-xl p-5 text-center">
                <div class="text-slate-400 text-xs uppercase tracking-wider mb-1">Sharpe penalty</div>
                <div class="text-3xl font-black text-red-400">−0.49</div>
                <div class="text-slate-400 text-xs mt-1">0.95 → 0.46 risk-adjusted drag</div>
              </div>
            </div>
          </div>

          <!-- The punchline -->
          <div class="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600 rounded-2xl p-8">
            <div class="flex items-start gap-4">
              <div class="text-4xl flex-shrink-0">🧮</div>
              <div>
                <h3 class="text-white font-bold text-lg mb-3">The Math Is Unavoidable</h3>
                <p class="text-slate-300 leading-relaxed mb-4">
                  Phase 2's AI signal is so strong that weekly trading still outperforms the S&P 500 — but look at the cost. To chase an extra <span class="text-slate-300 font-bold">11% in raw returns</span>, you have to pay <span class="text-red-400 font-bold">130% more in broker fees</span>, execute 332 extra trades, and <span class="text-red-400 font-bold">cut your risk-adjusted Sharpe Ratio in half</span>. We optimized InvestBuddy for your peace of mind, not your broker's commission quota.
                </p>
                <div class="bg-slate-900/60 rounded-lg px-5 py-3 text-sm font-mono text-slate-300">
                  <span class="text-yellow-400">Extra friction</span> = ($1,224 − $533) ÷ $10,000 = <span class="text-red-400 font-bold">6.91% of capital</span><br>
                  <span class="text-yellow-400">Sharpe penalty</span> = 0.95 → 0.46 = <span class="text-red-400 font-bold">−0.49 risk-adjusted cost</span> ← frequency tax
                </div>
                <p class="text-slate-400 text-sm mt-4 italic">
                  Fee assumptions: $1.00 flat commission per trade (any U.S. broker) + 0.05% slippage per execution side.
                  Applies to sell + buy legs at every rebalance. ETF benchmarks (VOO, QQQ, RSP, 60/40) assume zero transaction costs — a conservative assumption that actually <em>disadvantages</em> InvestBuddy.
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- YMYL Compliance: Financial Disclaimer Section -->
      <div class="mt-16 border-t border-slate-200 pt-8">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div class="bg-amber-50 border-2 border-amber-200 rounded-xl p-8">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-xl font-bold text-amber-900 mb-4">⚠️ Important Disclaimer</h3>
                <div class="text-slate-700 space-y-3 text-sm">
                  <p>
                    <strong>Not Financial Advice:</strong> InvestBuddy provides quantitative data, analysis, and AI-driven model portfolios for informational and educational purposes only. Nothing on this page constitutes personalized investment advice, a recommendation to buy or sell securities, or an offer to provide advisory services.
                  </p>
                  <p>
                    <strong>Past Performance Disclaimer:</strong> Historical backtest results are hypothetical and do not guarantee future performance. The benchmark comparisons shown (2022-2025) represent simulated performance calculated using historical data. Actual results will differ due to market conditions, timing, execution costs, and individual circumstances.
                  </p>
                  <p>
                    <strong>Historical Simulation Notice:</strong> This is a <strong>historical simulation</strong>, not a true out-of-sample forward test. The model's parameters were trained on data that partially overlaps with the 2023–2025 portion of the simulation period. The 2022 period (where InvestBuddy fell harder than VOO, reaching –29% by May 2022) reflects genuine out-of-distribution model behavior. True forward testing from March 2026 is ongoing.
                  </p>
                  <p>
                    <strong>Concentration Risk Disclosure:</strong> InvestBuddy holds 5 stocks vs. VOO's ~500. A Monte Carlo analysis (1,000 random 5-stock portfolios, same period and universe) showed the median random portfolio returned +50.72% — meaning concentration alone adds approximately +8pp over VOO before any AI selection. InvestBuddy's +106.12% sits at the <strong>77.7th percentile</strong> of random portfolios, reflecting real but moderate model edge. ~1 in 4 random portfolios achieved similar or better returns.
                  </p>
                  <p>
                    <strong>Risk Disclosure:</strong> All investing carries risk of loss, including loss of principal. Stocks and equity-based strategies are subject to market volatility. The maximum drawdown shown (–37.51% for InvestBuddy) represents a significant temporary loss that occurred during the test period. Future drawdowns may be larger.
                  </p>
                  <p>
                    <strong>Methodology Note:</strong> Transaction costs ($1/trade + 0.05% slippage) are included in all InvestBuddy results. ETF benchmarks assume zero transaction costs for maximum comparability. The September 2025 portfolio gain (+21.1% in 6 weeks, driven by MU +55% and UNH +36%) was independently audited against live Twelve Data prices and confirmed as a genuine market move with no data artifacts.
                  </p>
                  <p>
                    <strong>Consult a Professional:</strong> Before making investment decisions, consult a licensed financial advisor who understands your unique financial situation, goals, risk tolerance, and tax circumstances.
                  </p>
                  <div class="mt-4 pt-4 border-t border-amber-200 text-xs text-slate-600">
                    <p><strong>Research Attribution:</strong> Analysis prepared by InvestBuddy Research Team | Phase 3 Retrain #7: May 12, 2026 | Phase 2 Comprehensive: March 11, 2026 | <a href="/terms-of-service" class="text-indigo-600 hover:underline">Terms of Service</a> | <a href="/privacy-policy" class="text-indigo-600 hover:underline">Privacy Policy</a></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { logger } from '@/utils/logger'

// SEO Configuration Helper
const updateMetaTags = () => {
  // Update document title
  document.title = 'AI Stock Prediction Benchmark: InvestBuddy vs VOO, RSP, QQQ (2022-2025)'
  
  // Helper function to set or create meta tag
  const setMetaTag = (selector: string, attribute: string, content: string) => {
    let metaTag = document.querySelector(selector)
    if (!metaTag) {
      metaTag = document.createElement('meta')
      if (selector.includes('property')) {
        metaTag.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '')
      } else if (selector.includes('name')) {
        metaTag.setAttribute('name', selector.match(/name="([^"]+)"/)?.[1] || '')
      }
      document.head.appendChild(metaTag)
    }
    metaTag.setAttribute(attribute, content)
  }
  
  // Description and keywords
  setMetaTag('meta[name="description"]', 'content', 'InvestBuddy\'s Retrain #7 multitask LSTM (Phase 3) achieved +60.8% return vs RSP +35.74% (+25.05pp alpha) in a 2023-2025 validation-split backtest. 60.78% OOS directional accuracy, 47k parameters, Sharpe 0.78. Hard OOS Q4 2026.')
  setMetaTag('meta[name="keywords"]', 'content', 'AI stock predictions, machine learning investing, LSTM stock model, backtest results, VOO vs AI, S&P 500 benchmark, equal weight ETF, QQQ comparison, 60/40 portfolio, algorithmic trading performance, walk-forward validation, quantitative investing')
  setMetaTag('meta[name="author"]', 'content', 'InvestBuddy Research Team')
  setMetaTag('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')
  
  // Open Graph tags
  setMetaTag('meta[property="og:type"]', 'content', 'article')
  setMetaTag('meta[property="og:url"]', 'content', 'https://www.investbuddy.ai/research/benchmarks')
  setMetaTag('meta[property="og:title"]', 'content', 'InvestBuddy Retrain #7: +25.05pp Alpha vs RSP Equal-Weight (Phase 3 Validation)')
  setMetaTag('meta[property="og:description"]', 'content', 'Phase 3 multitask LSTM (Retrain #7): +60.8% return, +25.05pp vs RSP, 60.78% OOS win rate, 47k parameters (67% leaner than Phase 2). Soft-OOS validation split. Hard OOS Q4 2026.')
  setMetaTag('meta[property="og:image"]', 'content', 'https://www.investbuddy.ai/comprehensive-benchmark-og.png')
  setMetaTag('meta[property="article:published_time"]', 'content', '2026-02-26T00:00:00Z')
  setMetaTag('meta[property="article:modified_time"]', 'content', '2026-02-28T00:00:00Z')
  setMetaTag('meta[property="article:author"]', 'content', 'InvestBuddy Research Team')
  
  // Twitter Card
  setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image')
  setMetaTag('meta[name="twitter:url"]', 'content', 'https://www.investbuddy.ai/research/benchmarks')
  setMetaTag('meta[name="twitter:title"]', 'content', 'InvestBuddy Retrain #7: +25.05pp Alpha vs RSP (Phase 3 LSTM)')
  setMetaTag('meta[name="twitter:description"]', 'content', '+60.8% vs RSP +35.74%. 60.78% OOS win rate. 47k parameters — 67% leaner than Phase 2. Soft-OOS validation split, Hard OOS Q4 2026.')
  setMetaTag('meta[name="twitter:image"]', 'content', 'https://www.investbuddy.ai/comprehensive-benchmark-twitter.png')
  
  // Canonical URL
  let canonical = document.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', 'https://www.investbuddy.ai/research/benchmarks')
  
  // Add JSON-LD structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]')
  if (existingScript) {
    existingScript.remove()
  }
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ScholarlyArticle',
    'headline': 'Comprehensive AI Stock Prediction Benchmark: InvestBuddy vs VOO, RSP, QQQ, and 60/40 Portfolios',
    'description': 'Institutional-grade walk-forward backtest (2022-2025) demonstrating InvestBuddy\'s Crisis Alpha LSTM model beating major ETF benchmarks with rigorous data leakage prevention.',
    'image': 'https://www.investbuddy.ai/comprehensive-benchmark-og.png',
    'author': {
      '@type': 'Organization',
      'name': 'InvestBuddy',
      'url': 'https://www.investbuddy.ai'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'InvestBuddy',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://www.investbuddy.ai/logo.png'
      }
    },
    'datePublished': '2026-02-26',
    'dateModified': '2026-02-28',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': 'https://www.investbuddy.ai/research/benchmarks'
    },
    'about': [
      {
        '@type': 'Thing',
        'name': 'Machine Learning Stock Prediction'
      },
      {
        '@type': 'Thing',
        'name': 'LSTM Neural Networks'
      },
      {
        '@type': 'Thing',
        'name': 'Quantitative Finance'
      }
    ],
    'keywords': 'AI stock predictions, LSTM model, walk-forward backtest, VOO benchmark, equal weight ETF, Nasdaq-100 comparison, 60/40 portfolio, algorithmic trading',
    'citation': [
      {
        '@type': 'ScholarlyArticle',
        'name': 'Walk-Forward Validation Methodology',
        'url': 'https://www.investbuddy.ai/research/validation-report'
      }
    ],
    'isAccessibleForFree': true,
    'abstract': 'InvestBuddy\'s Crisis Alpha LSTM model was tested against four major investment benchmarks (VOO, RSP, QQQ, 60/40) using a robust walk-forward methodology with zero data leakage. Training period: 2012-2021 (45,900 sequences). Out-of-sample test period: 2022-2025 (315 trades). Results: +15.93% alpha vs S&P 500, +41.10% alpha vs equal-weight S&P 500, +5.76% alpha vs Nasdaq-100, +40.07% alpha vs traditional 60/40 portfolio. Risk-adjusted performance: 0.56 Sharpe ratio (best among all benchmarks), -23.93% maximum drawdown (despite 100% equity exposure).'
  })
  document.head.appendChild(script)
}

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule])

// Types
interface BenchmarkSnapshot {
  date: string
  investbuddy_value: number
  voo_value: number | null
  rsp_value: number | null
  qqq_value: number | null
  '60_40_value': number | null
  investbuddy_picks: string
}

interface BenchmarkSummary {
  start_date: string
  end_date: string
  total_months: number
  final_returns: {
    investbuddy: number
    voo: number
    rsp: number
    qqq: number
    '60_40': number
  }
}

// State
const loading = ref(true)
const error = ref<string | null>(null)
const snapshots = ref<BenchmarkSnapshot[]>([])
const summary = ref<BenchmarkSummary>({
  start_date: '',
  end_date: '',
  total_months: 0,
  final_returns: {
    investbuddy: 58.70,
    voo: 42.77,
    rsp: 17.60,
    qqq: 52.94,
    '60_40': 18.63
  }
})
const showInstitutionalBenchmarks = ref(false)
const gridApi = ref<GridApi | null>(null)

// AG Grid Configuration
const defaultColDef: ColDef = {
  sortable: true,
  filter: false,
  resizable: true,
  flex: 1,
  minWidth: 120,
  tooltipValueGetter: (params: any) => {
    // Enable tooltips for all cells - shows full value on hover
    if (params.value == null) return 'N/A'
    return params.valueFormatted || params.value
  }
}

const columnDefs = ref<ColDef[]>([
  {
    field: 'date',
    headerName: 'Rebalance Date',
    pinned: 'left',
    minWidth: 140,
    filter: true,
    valueFormatter: (params: any) => {
      if (!params.value) return ''
      const date = new Date(params.value)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    cellStyle: { fontWeight: '500', backgroundColor: '#f8fafc' }
  },
  {
    headerName: 'InvestBuddy AI',
    headerClass: 'text-center',
    children: [
      {
        field: 'investbuddy_value',
        headerName: 'Value ($10K Start)',
        minWidth: 160,
        valueFormatter: (params: any) => {
          if (params.value == null) return 'N/A'
          return `$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        cellStyle: { fontWeight: 'bold', color: '#16a34a', backgroundColor: '#f0fdf4' },
        type: 'rightAligned'
      },
      {
        field: 'investbuddy_picks',
        headerName: 'Top 5 Picks',
        minWidth: 220,
        cellRenderer: (params: any) => {
          const picks = params.value
          if (!picks || picks === 'TBD' || picks === 'N/A') {
            return '<span class="text-slate-400 italic text-xs">Held from prior rebalance</span>'
          }
          // Special styling for CASH positions
          if (picks === 'CASH') {
            return '<span class="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded border border-yellow-300">💵 CASH</span>'
          }
          // Split tickers and render as badges
          const tickers = picks.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0)
          return tickers.map((ticker: string) => 
            `<span class="inline-block px-2 py-1 mr-1 mb-1 bg-indigo-100 text-indigo-800 text-xs font-semibold rounded border border-indigo-300">${ticker}</span>`
          ).join('')
        },
        cellStyle: { lineHeight: '1.8' }
      }
    ]
  },
  {
    headerName: 'Benchmark Portfolios (Same $10K Start)',
    headerClass: 'text-center',
    children: [
      {
        field: 'voo_value',
        headerName: 'S&P 500 ETF (VOO)',
        hide: false,
        minWidth: 160,
        valueFormatter: (params: any) => {
          if (params.value == null) return 'N/A'
          return `$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        cellStyle: { color: '#2563eb', fontWeight: '500' },
        type: 'rightAligned'
      },
      {
        field: 'rsp_value',
        headerName: 'Equal Weight ETF (RSP)',
        hide: true,
        minWidth: 180,
        valueFormatter: (params: any) => {
          if (params.value == null) return 'N/A'
          return `$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        cellStyle: { color: '#9333ea', fontWeight: '500' },
        type: 'rightAligned'
      },
      {
        field: 'qqq_value',
        headerName: 'Nasdaq-100 ETF (QQQ)',
        hide: true,
        minWidth: 180,
        valueFormatter: (params: any) => {
          if (params.value == null) return 'N/A'
          return `$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        cellStyle: { color: '#ea580c', fontWeight: '500' },
        type: 'rightAligned'
      },
      {
        field: '60_40_value',
        headerName: '60/40 Portfolio',
        hide: true,
        minWidth: 160,
        valueFormatter: (params: any) => {
          if (params.value == null) return 'N/A'
          return `$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        cellStyle: { color: '#14b8a6', fontWeight: '500' },
        type: 'rightAligned'
      }
    ]
  }
])

// Computed
const totalSnapshots = computed(() => snapshots.value.length)
const displaySnapshots = computed(() => {
  // Show in reverse chronological order (newest first)
  return [...snapshots.value].reverse()
})

// Alpha calculations (excess return vs benchmarks)
const alphaVsVOO = computed(() => {
  const ib = summary.value.final_returns?.investbuddy || 0
  const voo = summary.value.final_returns?.voo || 0
  return ib - voo
})

const alphaVsRSP = computed(() => {
  const ib = summary.value.final_returns?.investbuddy || 0
  const rsp = summary.value.final_returns?.rsp || 0
  return ib - rsp
})

const alphaVsQQQ = computed(() => {
  const ib = summary.value.final_returns?.investbuddy || 0
  const qqq = summary.value.final_returns?.qqq || 0
  return ib - qqq
})

const alphaVs6040 = computed(() => {
  const ib = summary.value.final_returns?.investbuddy || 0
  const sixty_forty = summary.value.final_returns?.['60_40'] || 0
  return ib - sixty_forty
})

// Methods
const fetchData = async () => {
  loading.value = true
  error.value = null
  
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    const response = await fetch(`${apiUrl}/api/research/benchmarks`)
    
    if (!response.ok) {
      throw new Error(`API returned ${response.status}: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to load benchmark data')
    }
    
    snapshots.value = data.data?.monthly_snapshots || []
    summary.value = data.data?.summary || summary.value
    
    logger.info(`Loaded ${snapshots.value.length} benchmark snapshots`)
  } catch (err: any) {
    error.value = err.message
    logger.error('Failed to fetch benchmark data:', err)
  } finally {
    loading.value = false
  }
}

const toggleInstitutionalBenchmarks = () => {
  showInstitutionalBenchmarks.value = !showInstitutionalBenchmarks.value
  
  // Toggle column visibility using AG Grid API
  if (gridApi.value) {
    gridApi.value.setColumnsVisible(
      ['rsp_value', 'qqq_value', '60_40_value'],
      showInstitutionalBenchmarks.value
    )
  }
  
  logger.info(`Institutional benchmarks ${showInstitutionalBenchmarks.value ? 'shown' : 'hidden'}`)
}

const onGridReady = (params: GridReadyEvent) => {
  gridApi.value = params.api
  logger.info('AG Grid initialized successfully')
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatCurrency = (value: number | null): string => {
  if (value === null) return 'N/A'
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Lifecycle
onMounted(() => {
  updateMetaTags()
  fetchData()
})
</script>

<style scoped>
/* AG Grid theme will be imported in Task 4 */

/* AG Grid tooltip override */
:deep(.ag-tooltip) {
  background-color: #1e293b !important;
  color: #f1f5f9 !important;
  border: 1px solid #334155 !important;
  border-radius: 8px !important;
  padding: 6px 12px !important;
  font-size: 13px !important;
  font-family: inherit !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35) !important;
  max-width: 320px !important;
  line-height: 1.45 !important;
}

:deep(.ag-tooltip-hiding) {
  opacity: 0 !important;
  transition: opacity 0.15s ease !important;
}
</style>
